<template src="./template.html"></template>
<script>
import Promise from 'bluebird'
import { mapState } from 'vuex'
import _filter from 'lodash/filter'
import _find from 'lodash/find'
import _extend from 'lodash/extend'

import paymentProfileService from 'api/paymentProfile'
import store from 'store'
import * as mutationTypes from 'store/mutationTypes'
import stripeLib from 'lib/stripe'
import PAYMENT_TYPES from 'constants/paymentTypes'

export default {
  name: 'payment-profile',
  props: {
    booking: {
      type: Object,
      required: false,
    },
  },
  data() {
    return {
      loading: false,
      showPaymentMethods: true,
      showNewPaymentMethod: false,
      selectedPaymentProfile: {},
      newPaymentMethodForm: {
        ccNumber: null,
      },
      paymentProfiles: [],
      stripeCard: null,
      paymentType: null,
      PAYMENT_TYPES,
    }
  },
  computed: {
    ...mapState({
      loggedIn: (state) => state.auth.loggedIn,
      user: (state) => state.user.current,
      venue: (state) => state.venue.current,
      stripePaymentRequest: (state) => state.checkout.stripePaymentRequest,
      stripePaymentRequestResult: (state) => state.checkout.stripePaymentRequestResult,
    }),
  },
  watch: {
    paymentType(val) {
      this.$emit('paymentTypeChanged', val)
    },
  },
  methods: {
    validate() {
      return Promise.props({
        formValid: this.$validator.validateAll(),
        stripeCardValid: this.$refs.stripeCard ? this.$refs.stripeCard.validatePaymentProfile() : true,
      }).then(({ formValid, stripeCardValid }) => {
        return formValid && !!stripeCardValid
      })
    },
    toggleShowNewPaymentMethod() {
      this.showNewPaymentMethod = true
      this.showPaymentMethods = false
    },
    selectPaymentProfile(paymentProfile) {
      this.selectedPaymentProfile = paymentProfile

      this.$emit('input', paymentProfile)
      this.$emit('change', paymentProfile)
    },
    onPaymentMethodCancelled() {
      this.showNewPaymentMethod = false
      this.showPaymentMethods = true
    },
    savePaymentProfile() {
      let promise

      if (this.paymentType === PAYMENT_TYPES.CREDIT_CARD) {
        promise = this.$refs.stripeCard ? this.$refs.stripeCard.savePaymentProfile() : Promise.resolve()
      } else if (this.paymentType !== PAYMENT_TYPES.CREDIT_CARD) {
        promise = this.createStripePaymentRequestPaymentProfile()
      }

      if (!promise) {
        return Promise.reject(new Error('Payment type error'))
      }

      return promise.tap((paymentProfile) => {
        if (paymentProfile) {
          this.paymentProfiles.push(paymentProfile)

          this.selectPaymentProfile(paymentProfile)
        }
      })
    },
    listPaymentProfiles() {
      if (!this.user || !this.loggedIn) {
        this.toggleShowNewPaymentMethod()

        return Promise.resolve([])
      }

      return paymentProfileService
        .listByUserId(this.user.id, {
          providerType: 'STRIPE',
        })
        .then((paymentProfiles) => {
          this.paymentProfiles = paymentProfiles

          this.paymentProfiles = _filter(paymentProfiles, {
            providerType: 'STRIPE',
          })

          if (this.paymentProfiles.length) {
            this.selectedPaymentProfile =
              _find(this.paymentProfiles, {
                default: true,
              }) || this.paymentProfiles[0]

            this.$emit('input', this.selectedPaymentProfile)
          } else {
            this.toggleShowNewPaymentMethod()
          }
        })
    },
    onMutation(mutation) {
      if (mutation.type === mutationTypes.SET_CURRENT_USER && this.user) {
        this.loading = true

        this.listPaymentProfiles()
          .then(() => {
            this.loading = false
          })
          .catch(() => {
            this.loading = false
          })
      }
    },
    createStripePaymentRequestPaymentProfile() {
      if (this.paymentType === PAYMENT_TYPES.CREDIT_CARD) {
        throw new Error(`cannot get for paymentType : ${this.paymentType}`)
      }

      let paymentResponse

      return Promise.fromCallback((callback) => {
        this.stripePaymentRequest.on('token', (result) => {
          return callback(null, result)
        })

        this.stripePaymentRequest.on('cancel', () => {
          return callback(new Error('Payment request cancelled'))
        })

        this.stripePaymentRequest.show()
      })
        .then((result) => {
          paymentResponse = result

          const providerToken = paymentResponse.token.id

          return paymentProfileService.create({
            providerToken,
            providerType: 'STRIPE',
          })
        })
        .then((paymentProfile) => {
          paymentResponse.complete('success')

          return _extend(paymentProfile, {
            payerName: paymentResponse.payerName,
            payerEmail: paymentResponse.payerEmail,
            payerPhone: paymentResponse.payerPhone,
          })
        })
    },
    initStripePaymentRequest() {
      const venue = store.state.venue.current || {}

      const total = Math.round(this.booking.total) * 100

      const stripePaymentRequestOptions = {
        total: {
          amount: total,
          label: 'Total',
        },
        displayItems: this.booking.items.map((item) => {
          return {
            amount: stripeLib.convertNumberToSubUnit(item.price),
            label: item.name,
            pending: true, //means we might change this amount, i.e. processing fees
          }
        }),
        country: (venue.address && venue.address.country ? venue.address.country : 'US').toUpperCase(),
        currency: (venue.currencyCode || '').toLowerCase(),
        requestPayerName: true,
        requestPayerEmail: true,
        requestPayerPhone: true,
      }

      const stripePaymentRequest = stripeLib.getPaymentRequest(stripePaymentRequestOptions)

      this.$store.dispatch('setCheckoutStripePaymentRequest', stripePaymentRequest)

      return stripePaymentRequest
        .canMakePayment()
        .then((result) => {
          return this.$store.dispatch('setCheckoutStripePaymentRequestResult', result)
        })
        .then(() => {
          if (!this.stripePaymentRequestResult) {
            this.paymentType = PAYMENT_TYPES.CREDIT_CARD
          }
        })
    },
  },
  created() {
    this.storeUnsubscribe = store.subscribe(this.onMutation)

    this.listPaymentProfiles()

    if (this.booking) {
      this.initStripePaymentRequest()
    } else {
      this.paymentType = PAYMENT_TYPES.CREDIT_CARD
    }
  },
  updated() {
    this.$emit('updated')
  },
  destroyed() {
    this.storeUnsubscribe()
  },
}
</script>



// WEBPACK FOOTER //
// src/components/PaymentProfile/index.vue